import { createContext, useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import {
  buildPathWithLang,
  DEFAULT_LANG,
  getLangFromPath,
  getLanguageConfig,
  getQuestTypeLabel,
  type LangCode,
  stripLangPrefix,
} from './languages'

type TranslationValue = string | TranslationRecord

interface TranslationRecord {
  [key: string]: TranslationValue
}

const translations: Record<LangCode, Record<string, TranslationValue>> = {
  zh: {
    nav: {
      home: '首页',
      quests: '全部任务',
      study: '学习/Study',
      region: '地区浏览',
      type: '类型浏览',
      speakers: '角色统计',
      favorites: '收藏',
      about: '关于',
    },
    layout: {
      openSidebar: '打开侧边栏',
      siteTagline: '原神剧情任务浏览站',
      fallbackLabel: '任务世界',
      tipTitle: '提示',
      tipBody: '使用顶部搜索与过滤条件，快速定位任务并深链分享。',
    },
    theme: {
      toggle: '切换主题',
    },
    font: {
      label: '字体',
      customAria: '自定义字体',
      customPlaceholder: "例如：'LXGW WenKai', serif",
      hywenhei: 'HYWenHei（默认）',
      original: '原始默认（Inter / Noto Sans SC）',
      serif: '衬线 Serif',
      mono: '等宽 Mono',
      custom: '自定义',
    },
    dashboard: {
      title: '首页 · OnlineQuest',
      overviewTitle: '任务总览',
      overviewDesc: '覆盖全部剧情任务的统计摘要',
      shortcutsTitle: '快捷入口',
      shortcutsDesc: '按地区或类型探索剧情内容',
      recentTitle: '最近浏览',
      recentDesc: '从上次查看的位置继续',
      noRecent: '暂无浏览记录',
      favoritesTitle: '我的收藏',
      favoritesDesc: '快速访问你标记的剧情任务',
      noFavorites: '尚未收藏任务',
      stats: {
        totalQuests: '任务总数',
        hiddenQuests: '隐藏任务',
        dialogLines: '对话句数',
        narrationLines: '旁白句数',
        cutscenes: '过场数量',
        cutscenesWithSubtitles: '带字幕过场',
        region: '地区',
        type: '类型',
      },
    },
    quests: {
      title: '全部任务 · OnlineQuest',
      heading: '全部任务',
      subheading: '快速筛选与搜索所有任务',
      toggleFilters: '展开筛选',
      collapseFilters: '收起筛选',
      keywordSearch: '关键词搜索',
      keywordPlaceholder: '搜索任务标题、描述、角色名',
      filtersTitle: '筛选条件',
      regionLabel: '地区',
      typeLabel: '类型',
      advancedLabel: '高级筛选',
      showHidden: '显示隐藏任务',
      onlyCutscenes: '仅过场任务',
      onlySubtitles: '仅带字幕',
      onlyFavorites: '仅收藏',
      chapterLevel: '章节 / 等级',
      chapterPlaceholder: '章节关键字',
      levelMinPlaceholder: '推荐等级 ≥',
      levelMaxPlaceholder: '推荐等级 ≤',
      resultSummary: '共找到 {{count}} 个任务 · {{chapters}} 个章节',
    },
    region: {
      title: '{{region}} · 地区任务 · OnlineQuest',
      heading: '{{region}} · 地区任务',
      subheading: '分类型浏览该地区所有剧情',
      statsTitle: '地区统计',
      settingsTitle: '浏览设置',
      totalQuests: '任务总数',
      dialogLines: '对话句数',
      narrationLines: '旁白句数',
      cutscenes: '过场数量',
      showHidden: '显示隐藏任务',
      onlyCutscenes: '仅过场',
      onlySubtitles: '仅字幕',
      sortLabel: '排序',
      sortChapter: '章节',
      sortLevel: '推荐等级',
      sortTitle: '标题',
      questCount: '{{count}} 个任务',
    },
    type: {
      title: '{{typeLabel}} · OnlineQuest',
      heading: '{{typeLabel}} · 任务',
      subheading: '按地区进一步浏览任务',
      settingsTitle: '浏览设置',
      showHidden: '显示隐藏任务',
      onlyCutscenes: '仅过场',
      onlySubtitles: '仅字幕',
      sortLabel: '排序',
      sortChapter: '章节',
      sortLevel: '推荐等级',
      sortTitle: '标题',
      questCount: '{{count}} 个任务',
    },
    chapter: {
      title: '章节详情',
      backToChapters: '返回章节列表',
    },
    speakers: {
      title: '角色统计 · OnlineQuest',
      heading: '全站角色统计',
      subheading: '角色台词数排行与出现分布',
      searchLabel: '搜索角色',
      searchPlaceholder: '输入角色名',
      loadError: '角色数据加载失败，请稍后重试。',
      lineCount: '{{count}} 句',
    },
    speakerDetail: {
      title: '角色 {{id}} · OnlineQuest',
      unknownSpeaker: '未知角色',
      speakerId: '角色 ID：{{id}}',
      questsTitle: '出现任务',
      loadError: '角色数据加载失败，请稍后重试。',
      questCount: '{{count}} 个任务',
    },
    about: {
      title: '关于 · OnlineQuest',
      heading: '关于与数据版本',
      subheading: '数据来源与构建信息',
      siteInfoTitle: '站点生成信息',
      generatedAt: '生成时间：{{value}}',
      totalQuests: '任务总数：{{value}}',
      loading: '加载中...',
      unavailable: '暂无数据',
    },
    study: {
      title: '学习对照 · OnlineQuest',
      heading: '学习/Study',
      subheading: '跨语言对照剧情、步骤与奖励',
      studyLabel: '学习/Study',
      languageTitle: '语言设置',
      mainLang: '主语言',
      compareLangs: '对照语言',
      compareHint: '可选择多个语言列，至少保留一列。',
      questSelection: '选择任务',
      questIdLabel: 'Quest ID',
      questIdPlaceholder: '输入任务 ID',
      searchLabel: '搜索标题',
      searchPlaceholder: '输入关键词搜索任务',
      resultsTitle: '搜索结果',
      noResults: '暂无匹配结果',
      enterStudy: '进入学习',
      backToHome: '返回学习首页',
      overviewTitle: '任务概览对照',
      overview: {
        title: '标题',
        description: '描述',
        chapter: '章节',
        region: '地区',
        questType: '类型',
      },
      tabs: {
        dialogue: '剧情对照',
        steps: '步骤对照',
        speakers: '人物对照',
        rewards: '奖励对照',
      },
      flowBadge: '子任务 {{id}}',
      flowOrder: '顺序 {{order}}',
      flowOrderShort: '顺序 {{order}}',
      dialogueSegment: '对话片段',
      narrationTitle: '旁白对照',
      stepsTitle: '步骤对照',
      stepHeader: '步骤信息',
      subQuestId: '子任务 {{id}}',
      speakersTitle: '人物对照',
      speakerId: '角色 ID',
      speakerCount: '出现 {{count}} 次',
      rewardsTitle: '奖励对照',
      rewardId: '道具 ID',
      noDialogues: '暂无对话',
      noNarration: '暂无旁白',
      noSpeakers: '暂无角色',
      noRewards: '暂无奖励',
      missing: '—',
      invalidQuest: '任务 ID 无效。',
      loading: '加载中...',
      branchLabel: '分支',
      vocabTitle: '单词本 · OnlineQuest',
      vocabHeading: '单词本',
      vocabHint: '点击词条即可收藏到单词本。',
      glossLangLabel: '释义语言',
      sourceLangLabel: '来源语言',
      lookupTitle: '词条释义',
      lookupDefinition: '释义',
      lookupProvider: '来源',
      lookupUnknown: '未知',
      lookupEmpty: '(暂无释义)',
      lookupLoading: '查询中...',
      lookupApiError: '无法直连词典 API',
      lookupExternal: '打开外部词典/翻译',
      lookupSourceInfo: '来源信息',
      lookupQuestId: 'Quest ID：{{id}}',
      lookupNodeId: '节点 ID：{{id}}',
      lookupSubQuestId: '子任务 ID：{{id}}',
      lookupColumnLang: '列语言：{{lang}}',
      lookupSnippet: '文本片段',
      lookupClose: '关闭',
      vocabSave: '收藏',
      vocabRemove: '取消收藏',
      vocabAll: '全部',
      vocabSourceLang: '来源语言筛选',
      vocabGlossLang: '释义语言筛选',
      vocabSearch: '搜索词条',
      vocabSearchPlaceholder: '输入关键词',
      vocabExportJson: '导出 JSON',
      vocabExportCsv: '导出 CSV',
      vocabGroupToday: '今天',
      vocabGroupYesterday: '昨天',
      vocabEmpty: '暂无收藏词条',
      vocabSources: '来源记录',
    },
    notFound: {
      heading: '页面未找到',
      body: '请检查链接或返回首页。',
      backHome: '返回首页',
    },
    quest: {
      backToList: '返回任务列表',
      backToDetail: '返回任务详情',
      hidden: '隐藏任务',
      favorite: '收藏',
      favorited: '已收藏',
      readerMode: '阅读模式',
      region: '地区',
      chapter: '章节',
      recommendLevel: '推荐等级',
      dialogCount: '对话句数',
      needLevel: '需求等级',
      cutscenes: '过场',
      cutscenesYes: '有',
      cutscenesNo: '无',
      preQuests: '前置任务',
      noPreQuests: '无前置任务',
      nextQuests: '后续任务',
      noNextQuests: '无后续任务',
      tabs: {
        readable: '可读剧情',
        dialogs: '对话',
        flow: '步骤',
        narration: '旁白',
        cutscenes: '过场',
        rewards: '奖励',
        raw: '原始 JSON',
      },
      noQuestData: '暂无任务数据',
      dialogBrowse: '对话浏览',
      allSpeakers: '全部角色',
      searchDialogPlaceholder: '搜索当前任务对话',
      noDialogMatch: '没有匹配的对话',
      unmappedDialogs: '未映射对话',
      flowTitle: '步骤 Flow',
      flowHidden: '隐藏步骤',
      flowPublic: '公开步骤',
      conditionDetails: '条件详情',
      narrationTitle: '旁白 Narration',
      noNarration: '暂无旁白数据',
      cutscenesTitle: '过场 Cutscenes',
      noCutscenes: '暂无过场数据',
      rewardsTitle: '奖励',
      noRewards: '暂无奖励信息',
      rawTitle: '原始 JSON',
      copyJson: '复制任务 JSON',
      downloadSubtitle: '下载字幕',
    },
    reader: {
      title: '{{title}} · 阅读模式 · OnlineQuest',
      fallbackTitle: '任务 {{id}}',
      themeToggle: '主题',
      copyText: '复制纯文本',
      copyMarkdown: '复制 Markdown',
      fontSize: '字号',
      lineHeight: '行距',
      dialogAndNarration: '对话与旁白',
      stepLabel: '步骤 {{step}}：子任务 {{subQuestId}}',
      noDialogInStep: '（此步骤无对话）',
      narrationLabel: '[旁白] {{text}}',
      stepHeader: '子任务 {{subQuestId}} · Step {{step}}',
      narrationSection: '旁白',
      speakerFallback: '未知角色',
      branchLabel: '分支 {{index}}',
    },
    questJson: {
      speakerUnknown: '未知角色',
      nodeLabel: '节点 {{id}}',
      branchLabel: '分支 {{index}}',
      noDialogInBranch: '该分支没有可展示的对话。',
      noJoinDetected: '未检测到合流点，主线展示在此停止。',
      noDialogNode: '此对话没有可显示的节点。',
      noQuestData: '暂无任务数据',
      infoTitle: '任务信息',
      infoType: '任务类型',
      infoChapter: '所属章节',
      infoRegion: '所属地区',
      infoRecommend: '推荐等级',
      infoNeed: '需求等级',
      infoMainQuestId: '主线 ID',
      infoPreMainQuest: '前置主任务',
      infoNextMainQuest: '后接主任务',
      none: '无',
      infoRewards: '任务奖励',
      noRewards: '暂无奖励',
      flowTitle: '剧情流程',
      noFlow: '暂无流程数据',
      stepTitle: '步骤 {{order}}：子任务 {{subQuestId}}',
      expandConditions: '展开任务条件',
      cutsceneTitle: '过场动画',
      cutsceneFallback: '未知编号',
      dialogTitle: '对话',
      noDialogStep: '（此步骤无对话）',
      startNode: '起点节点 {{id}}',
    },
    questCard: {
      chapterFallback: '未分类章节',
      chapterTitleFallback: '章节未命名',
      recommendLevel: '推荐等级',
      hidden: '隐藏',
      dialogCount: '{{count}} 对话',
      cutsceneHas: '含过场',
      cutsceneNone: '无过场',
      favorite: '收藏',
      favorited: '已收藏',
    },
    chapterCard: {
      uncategorized: '未分类章节',
      unnamed: '章节未命名',
      questCount: '{{count}} 个任务',
    },
    questSection: {
      questCount: '{{count}} 个任务',
      collapse: '收起',
      expand: '展开',
    },
    questCondition: {
      conditionLabel: '条件 {{index}}',
      finishCond: '完成条件',
      triggerCond: '触发条件',
      execActions: '执行动作',
      guide: '引导信息',
      actionLabel: '动作 {{index}}',
      otherFields: '其他字段',
      expandJson: '展开 JSON',
      expandConditions: '展开任务条件',
    },
    common: {
      questFallback: '任务 {{id}}',
      questsCount: '{{count}} 个任务',
    },
  },
  en: {
    nav: {
      home: 'Home',
      quests: 'All Quests',
      study: 'Study',
      region: 'Browse by Region',
      type: 'Browse by Type',
      speakers: 'Speakers',
      favorites: 'Favorites',
      about: 'About',
    },
    layout: {
      openSidebar: 'Open sidebar',
      siteTagline: 'Genshin Quest Archive',
      fallbackLabel: 'Quest World',
      tipTitle: 'Tip',
      tipBody: 'Use the top search and filters to jump to quests and share deep links.',
    },
    theme: {
      toggle: 'Toggle theme',
    },
    font: {
      label: 'Font',
      customAria: 'Custom font',
      customPlaceholder: "e.g. 'LXGW WenKai', serif",
      hywenhei: 'HYWenHei (Default)',
      original: 'Original default (Inter / Noto Sans SC)',
      serif: 'Serif',
      mono: 'Monospace',
      custom: 'Custom',
    },
    dashboard: {
      title: 'Home · OnlineQuest',
      overviewTitle: 'Quest Overview',
      overviewDesc: 'A summary of all story quests',
      shortcutsTitle: 'Quick Access',
      shortcutsDesc: 'Explore quests by region or type',
      recentTitle: 'Recently Viewed',
      recentDesc: 'Continue from where you left off',
      noRecent: 'No recent views yet',
      favoritesTitle: 'Favorites',
      favoritesDesc: 'Jump back to quests you starred',
      noFavorites: 'No favorite quests yet',
      stats: {
        totalQuests: 'Total quests',
        hiddenQuests: 'Hidden quests',
        dialogLines: 'Dialog lines',
        narrationLines: 'Narration lines',
        cutscenes: 'Cutscenes',
        cutscenesWithSubtitles: 'Cutscenes with subtitles',
        region: 'Region',
        type: 'Type',
      },
    },
    quests: {
      title: 'All Quests · OnlineQuest',
      heading: 'All Quests',
      subheading: 'Filter and search every quest',
      toggleFilters: 'Show filters',
      collapseFilters: 'Hide filters',
      keywordSearch: 'Keyword search',
      keywordPlaceholder: 'Search by title, description, or speaker',
      filtersTitle: 'Filters',
      regionLabel: 'Region',
      typeLabel: 'Type',
      advancedLabel: 'Advanced filters',
      showHidden: 'Show hidden quests',
      onlyCutscenes: 'Cutscenes only',
      onlySubtitles: 'Subtitles only',
      onlyFavorites: 'Favorites only',
      chapterLevel: 'Chapter / Level',
      chapterPlaceholder: 'Chapter keyword',
      levelMinPlaceholder: 'Recommended level ≥',
      levelMaxPlaceholder: 'Recommended level ≤',
      resultSummary: 'Found {{count}} quests · {{chapters}} chapters',
    },
    region: {
      title: '{{region}} · Region · OnlineQuest',
      heading: '{{region}} · Region Quests',
      subheading: 'Browse every quest in this region',
      statsTitle: 'Region stats',
      settingsTitle: 'Browse settings',
      totalQuests: 'Total quests',
      dialogLines: 'Dialog lines',
      narrationLines: 'Narration lines',
      cutscenes: 'Cutscenes',
      showHidden: 'Show hidden quests',
      onlyCutscenes: 'Cutscenes only',
      onlySubtitles: 'Subtitles only',
      sortLabel: 'Sort',
      sortChapter: 'Chapter',
      sortLevel: 'Recommended level',
      sortTitle: 'Title',
      questCount: '{{count}} quests',
    },
    type: {
      title: '{{typeLabel}} · OnlineQuest',
      heading: '{{typeLabel}} · Quests',
      subheading: 'Browse quests grouped by region',
      settingsTitle: 'Browse settings',
      showHidden: 'Show hidden quests',
      onlyCutscenes: 'Cutscenes only',
      onlySubtitles: 'Subtitles only',
      sortLabel: 'Sort',
      sortChapter: 'Chapter',
      sortLevel: 'Recommended level',
      sortTitle: 'Title',
      questCount: '{{count}} quests',
    },
    chapter: {
      title: 'Chapter Details',
      backToChapters: 'Back to chapters',
    },
    speakers: {
      title: 'Speakers · OnlineQuest',
      heading: 'Speaker Stats',
      subheading: 'Ranking and distribution of dialog lines',
      searchLabel: 'Search speakers',
      searchPlaceholder: 'Enter a speaker name',
      loadError: 'Failed to load speaker data. Please try again later.',
      lineCount: '{{count}} lines',
    },
    speakerDetail: {
      title: 'Speaker {{id}} · OnlineQuest',
      unknownSpeaker: 'Unknown speaker',
      speakerId: 'Speaker ID: {{id}}',
      questsTitle: 'Appears in quests',
      loadError: 'Failed to load speaker data. Please try again later.',
      questCount: '{{count}} quests',
    },
    about: {
      title: 'About · OnlineQuest',
      heading: 'About & Data Version',
      subheading: 'Data sources and build info',
      siteInfoTitle: 'Site build info',
      generatedAt: 'Generated at: {{value}}',
      totalQuests: 'Total quests: {{value}}',
      loading: 'Loading...',
      unavailable: 'Unavailable',
    },
    study: {
      title: 'Study · OnlineQuest',
      heading: 'Study',
      subheading: 'Compare dialogue, steps, speakers, and rewards across languages.',
      studyLabel: 'Study',
      languageTitle: 'Language setup',
      mainLang: 'Primary language',
      compareLangs: 'Comparison languages',
      compareHint: 'Select multiple columns; keep at least one.',
      questSelection: 'Select quest',
      questIdLabel: 'Quest ID',
      questIdPlaceholder: 'Enter quest ID',
      searchLabel: 'Search titles',
      searchPlaceholder: 'Search by quest title',
      resultsTitle: 'Results',
      noResults: 'No matching quests',
      enterStudy: 'Open study',
      backToHome: 'Back to Study',
      overviewTitle: 'Overview comparison',
      overview: {
        title: 'Title',
        description: 'Description',
        chapter: 'Chapter',
        region: 'Region',
        questType: 'Type',
      },
      tabs: {
        dialogue: 'Dialogue',
        steps: 'Steps',
        speakers: 'Speakers',
        rewards: 'Rewards',
      },
      flowBadge: 'Sub-quest {{id}}',
      flowOrder: 'Order {{order}}',
      flowOrderShort: 'Order {{order}}',
      dialogueSegment: 'Dialogue segment',
      narrationTitle: 'Narration comparison',
      stepsTitle: 'Step comparison',
      stepHeader: 'Step',
      subQuestId: 'Sub-quest {{id}}',
      speakersTitle: 'Speaker comparison',
      speakerId: 'Speaker ID',
      speakerCount: '{{count}} lines',
      rewardsTitle: 'Rewards comparison',
      rewardId: 'Item ID',
      noDialogues: 'No dialogue',
      noNarration: 'No narration',
      noSpeakers: 'No speakers',
      noRewards: 'No rewards',
      missing: '—',
      invalidQuest: 'Invalid quest ID.',
      loading: 'Loading...',
      branchLabel: 'Branch',
      vocabTitle: 'Vocab · OnlineQuest',
      vocabHeading: 'Vocabulary',
      vocabHint: 'Tap any term to save it to your vocab list.',
      glossLangLabel: 'Gloss language',
      sourceLangLabel: 'Source language',
      lookupTitle: 'Definition',
      lookupDefinition: 'Definition',
      lookupProvider: 'Provider',
      lookupUnknown: 'Unknown',
      lookupEmpty: '(No definition found)',
      lookupLoading: 'Looking up...',
      lookupApiError: 'Unable to reach dictionary API',
      lookupExternal: 'Open external dictionary/translation',
      lookupSourceInfo: 'Source info',
      lookupQuestId: 'Quest ID: {{id}}',
      lookupNodeId: 'Node ID: {{id}}',
      lookupSubQuestId: 'Sub-quest ID: {{id}}',
      lookupColumnLang: 'Column language: {{lang}}',
      lookupSnippet: 'Snippet',
      lookupClose: 'Close',
      vocabSave: 'Save',
      vocabRemove: 'Remove',
      vocabAll: 'All',
      vocabSourceLang: 'Filter by source language',
      vocabGlossLang: 'Filter by gloss language',
      vocabSearch: 'Search term',
      vocabSearchPlaceholder: 'Enter a keyword',
      vocabExportJson: 'Export JSON',
      vocabExportCsv: 'Export CSV',
      vocabGroupToday: 'Today',
      vocabGroupYesterday: 'Yesterday',
      vocabEmpty: 'No saved vocab yet',
      vocabSources: 'Sources',
    },
    notFound: {
      heading: 'Page not found',
      body: 'Check the link or return home.',
      backHome: 'Back home',
    },
    quest: {
      backToList: 'Back to quest list',
      backToDetail: 'Back to quest details',
      hidden: 'Hidden quest',
      favorite: 'Favorite',
      favorited: 'Favorited',
      readerMode: 'Reader mode',
      region: 'Region',
      chapter: 'Chapter',
      recommendLevel: 'Recommended level',
      dialogCount: 'Dialog lines',
      needLevel: 'Required level',
      cutscenes: 'Cutscenes',
      cutscenesYes: 'Yes',
      cutscenesNo: 'No',
      preQuests: 'Prerequisite quests',
      noPreQuests: 'No prerequisites',
      nextQuests: 'Next quests',
      noNextQuests: 'No follow-up quests',
      tabs: {
        readable: 'Readable Story',
        dialogs: 'Dialog',
        flow: 'Flow',
        narration: 'Narration',
        cutscenes: 'Cutscenes',
        rewards: 'Rewards',
        raw: 'Raw JSON',
      },
      noQuestData: 'No quest data available',
      dialogBrowse: 'Dialog browser',
      allSpeakers: 'All speakers',
      searchDialogPlaceholder: 'Search dialog in this quest',
      noDialogMatch: 'No matching dialog',
      unmappedDialogs: 'Unmapped dialog',
      flowTitle: 'Flow',
      flowHidden: 'Hidden step',
      flowPublic: 'Public step',
      conditionDetails: 'Condition details',
      narrationTitle: 'Narration',
      noNarration: 'No narration data',
      cutscenesTitle: 'Cutscenes',
      noCutscenes: 'No cutscenes data',
      rewardsTitle: 'Rewards',
      noRewards: 'No reward data',
      rawTitle: 'Raw JSON',
      copyJson: 'Copy quest JSON',
      downloadSubtitle: 'Download subtitles',
    },
    reader: {
      title: '{{title}} · Reader Mode · OnlineQuest',
      fallbackTitle: 'Quest {{id}}',
      themeToggle: 'Theme',
      copyText: 'Copy plain text',
      copyMarkdown: 'Copy Markdown',
      fontSize: 'Font size',
      lineHeight: 'Line height',
      dialogAndNarration: 'Dialog & narration',
      stepLabel: 'Step {{step}}: Sub quest {{subQuestId}}',
      noDialogInStep: '(No dialog in this step)',
      narrationLabel: '[Narration] {{text}}',
      stepHeader: 'Sub quest {{subQuestId}} · Step {{step}}',
      narrationSection: 'Narration',
      speakerFallback: 'Unknown',
      branchLabel: 'Branch {{index}}',
    },
    questJson: {
      speakerUnknown: 'Unknown',
      nodeLabel: 'Node {{id}}',
      branchLabel: 'Branch {{index}}',
      noDialogInBranch: 'No dialog available in this branch.',
      noJoinDetected: 'No join detected. The main route stops here.',
      noDialogNode: 'No dialog nodes available.',
      noQuestData: 'No quest data available',
      infoTitle: 'Quest info',
      infoType: 'Quest type',
      infoChapter: 'Chapter',
      infoRegion: 'Region',
      infoRecommend: 'Recommended level',
      infoNeed: 'Required level',
      infoMainQuestId: 'Main quest ID',
      infoPreMainQuest: 'Prerequisite main quests',
      infoNextMainQuest: 'Next main quests',
      none: 'None',
      infoRewards: 'Quest rewards',
      noRewards: 'No rewards',
      flowTitle: 'Quest flow',
      noFlow: 'No flow data',
      stepTitle: 'Step {{order}}: Sub quest {{subQuestId}}',
      expandConditions: 'Expand conditions',
      cutsceneTitle: 'Cutscenes',
      cutsceneFallback: 'Unknown ID',
      dialogTitle: 'Dialog',
      noDialogStep: '(No dialog in this step)',
      startNode: 'Start node {{id}}',
    },
    questCard: {
      chapterFallback: 'Uncategorized chapter',
      chapterTitleFallback: 'Untitled chapter',
      recommendLevel: 'Recommended level',
      hidden: 'Hidden',
      dialogCount: '{{count}} dialog',
      cutsceneHas: 'Cutscenes',
      cutsceneNone: 'No cutscenes',
      favorite: 'Favorite',
      favorited: 'Favorited',
    },
    chapterCard: {
      uncategorized: 'Uncategorized chapter',
      unnamed: 'Untitled chapter',
      questCount: '{{count}} quests',
    },
    questSection: {
      questCount: '{{count}} quests',
      collapse: 'Collapse',
      expand: 'Expand',
    },
    questCondition: {
      conditionLabel: 'Condition {{index}}',
      finishCond: 'Finish conditions',
      triggerCond: 'Trigger conditions',
      execActions: 'Execution actions',
      guide: 'Guidance',
      actionLabel: 'Action {{index}}',
      otherFields: 'Other fields',
      expandJson: 'Expand JSON',
      expandConditions: 'Expand conditions',
    },
    common: {
      questFallback: 'Quest {{id}}',
      questsCount: '{{count}} quests',
    },
  },
  ja: {
    nav: {
      home: 'ホーム',
      quests: '全任務',
      study: '学習',
      region: '地域別',
      type: '種類別',
      speakers: 'キャラ統計',
      favorites: 'お気に入り',
      about: 'このサイトについて',
    },
    layout: {
      openSidebar: 'サイドバーを開く',
      siteTagline: '原神ストーリー任務ビューア',
      fallbackLabel: '任務の世界',
      tipTitle: 'ヒント',
      tipBody: '上部の検索とフィルターで任務を絞り込み、リンク共有もできます。',
    },
    theme: {
      toggle: 'テーマ切替',
    },
    font: {
      label: 'フォント',
      customAria: 'カスタムフォント',
      customPlaceholder: "例：'LXGW WenKai', serif",
      hywenhei: 'HYWenHei（既定）',
      original: '既定（Inter / Noto Sans SC）',
      serif: 'セリフ',
      mono: '等幅',
      custom: 'カスタム',
    },
    dashboard: {
      title: 'ホーム · OnlineQuest',
      overviewTitle: '任務概要',
      overviewDesc: '全ストーリー任務の統計サマリー',
      shortcutsTitle: 'クイックアクセス',
      shortcutsDesc: '地域や種類から任務を探す',
      recentTitle: '最近見た任務',
      recentDesc: '前回の続きから再開',
      noRecent: '最近の履歴はありません',
      favoritesTitle: 'お気に入り',
      favoritesDesc: 'お気に入りの任務にすぐアクセス',
      noFavorites: 'お気に入りはまだありません',
      stats: {
        totalQuests: '任務数',
        hiddenQuests: '非表示任務',
        dialogLines: '会話行数',
        narrationLines: 'ナレーション行数',
        cutscenes: 'ムービー数',
        cutscenesWithSubtitles: '字幕付きムービー',
        region: '地域',
        type: '種類',
      },
    },
    quests: {
      title: '全任務 · OnlineQuest',
      heading: '全任務',
      subheading: '任務を絞り込み・検索',
      toggleFilters: 'フィルターを表示',
      collapseFilters: 'フィルターを隠す',
      keywordSearch: 'キーワード検索',
      keywordPlaceholder: '任務名・説明・登場人物で検索',
      filtersTitle: 'フィルター',
      regionLabel: '地域',
      typeLabel: '種類',
      advancedLabel: '高度なフィルター',
      showHidden: '非表示任務を表示',
      onlyCutscenes: 'ムービーのみ',
      onlySubtitles: '字幕付きのみ',
      onlyFavorites: 'お気に入りのみ',
      chapterLevel: '章 / レベル',
      chapterPlaceholder: '章キーワード',
      levelMinPlaceholder: '推奨レベル ≥',
      levelMaxPlaceholder: '推奨レベル ≤',
      resultSummary: '{{count}} 件の任務 · {{chapters}} 章',
    },
    region: {
      title: '{{region}} · 地域 · OnlineQuest',
      heading: '{{region}} · 地域任務',
      subheading: 'この地域の任務を一覧',
      statsTitle: '地域統計',
      settingsTitle: '閲覧設定',
      totalQuests: '任務数',
      dialogLines: '会話行数',
      narrationLines: 'ナレーション行数',
      cutscenes: 'ムービー数',
      showHidden: '非表示任務を表示',
      onlyCutscenes: 'ムービーのみ',
      onlySubtitles: '字幕付きのみ',
      sortLabel: '並び替え',
      sortChapter: '章',
      sortLevel: '推奨レベル',
      sortTitle: 'タイトル',
      questCount: '{{count}} 件',
    },
    type: {
      title: '{{typeLabel}} · OnlineQuest',
      heading: '{{typeLabel}} · 任務',
      subheading: '地域別に任務を閲覧',
      settingsTitle: '閲覧設定',
      showHidden: '非表示任務を表示',
      onlyCutscenes: 'ムービーのみ',
      onlySubtitles: '字幕付きのみ',
      sortLabel: '並び替え',
      sortChapter: '章',
      sortLevel: '推奨レベル',
      sortTitle: 'タイトル',
      questCount: '{{count}} 件',
    },
    chapter: {
      title: '章の詳細',
      backToChapters: '章一覧へ戻る',
    },
    speakers: {
      title: 'キャラ統計 · OnlineQuest',
      heading: 'キャラ統計',
      subheading: 'セリフ数ランキングと分布',
      searchLabel: 'キャラ検索',
      searchPlaceholder: 'キャラ名を入力',
      loadError: 'データの取得に失敗しました。しばらくしてから再試行してください。',
      lineCount: '{{count}} 行',
    },
    speakerDetail: {
      title: 'キャラ {{id}} · OnlineQuest',
      unknownSpeaker: '不明なキャラ',
      speakerId: 'キャラ ID：{{id}}',
      questsTitle: '登場任務',
      loadError: 'データの取得に失敗しました。しばらくしてから再試行してください。',
      questCount: '{{count}} 件',
    },
    about: {
      title: 'このサイトについて · OnlineQuest',
      heading: 'サイト情報とデータ版本',
      subheading: 'データソースとビルド情報',
      siteInfoTitle: '生成情報',
      generatedAt: '生成日時：{{value}}',
      totalQuests: '任務数：{{value}}',
      loading: '読み込み中...',
      unavailable: 'データなし',
    },
    study: {
      title: '学習 · OnlineQuest',
      heading: '学習/Study',
      subheading: '言語ごとの台詞・手順・人物・報酬を対照。',
      studyLabel: '学習',
      languageTitle: '言語設定',
      mainLang: '主言語',
      compareLangs: '対照言語',
      compareHint: '複数列を選択できます。最低1列は残してください。',
      questSelection: '任務選択',
      questIdLabel: 'クエストID',
      questIdPlaceholder: 'クエストIDを入力',
      searchLabel: 'タイトル検索',
      searchPlaceholder: 'タイトルで検索',
      resultsTitle: '検索結果',
      noResults: '該当する任務はありません',
      enterStudy: '学習へ',
      backToHome: '学習トップへ',
      overviewTitle: '概要対照',
      overview: {
        title: 'タイトル',
        description: '説明',
        chapter: '章',
        region: '地域',
        questType: '種類',
      },
      tabs: {
        dialogue: '物語対照',
        steps: '手順対照',
        speakers: '人物対照',
        rewards: '報酬対照',
      },
      flowBadge: 'サブ任務 {{id}}',
      flowOrder: '順序 {{order}}',
      flowOrderShort: '順序 {{order}}',
      dialogueSegment: '会話セグメント',
      narrationTitle: 'ナレーション対照',
      stepsTitle: '手順対照',
      stepHeader: '手順',
      subQuestId: 'サブ任務 {{id}}',
      speakersTitle: '人物対照',
      speakerId: 'キャラクターID',
      speakerCount: '{{count}} 行',
      rewardsTitle: '報酬対照',
      rewardId: 'アイテムID',
      noDialogues: '会話なし',
      noNarration: 'ナレーションなし',
      noSpeakers: 'キャラクターなし',
      noRewards: '報酬なし',
      missing: '—',
      invalidQuest: 'クエストIDが無効です。',
      loading: '読み込み中...',
      branchLabel: '分岐',
      vocabTitle: '単語帳 · OnlineQuest',
      vocabHeading: '単語帳',
      vocabHint: '単語をクリックして単語帳に保存できます。',
      glossLangLabel: '訳語言語',
      sourceLangLabel: '元言語',
      lookupTitle: '語釈',
      lookupDefinition: '意味',
      lookupProvider: '提供元',
      lookupUnknown: '不明',
      lookupEmpty: '(定義なし)',
      lookupLoading: '検索中...',
      lookupApiError: '辞書APIに接続できません',
      lookupExternal: '外部辞書/翻訳を開く',
      lookupSourceInfo: 'ソース情報',
      lookupQuestId: 'クエストID：{{id}}',
      lookupNodeId: 'ノードID：{{id}}',
      lookupSubQuestId: 'サブ任務ID：{{id}}',
      lookupColumnLang: '列言語：{{lang}}',
      lookupSnippet: 'テキスト断片',
      lookupClose: '閉じる',
      vocabSave: '保存',
      vocabRemove: '削除',
      vocabAll: 'すべて',
      vocabSourceLang: '元言語で絞り込み',
      vocabGlossLang: '訳語言語で絞り込み',
      vocabSearch: '単語検索',
      vocabSearchPlaceholder: 'キーワードを入力',
      vocabExportJson: 'JSONを書き出し',
      vocabExportCsv: 'CSVを書き出し',
      vocabGroupToday: '今日',
      vocabGroupYesterday: '昨日',
      vocabEmpty: '保存した単語はありません',
      vocabSources: 'ソース履歴',
    },
    notFound: {
      heading: 'ページが見つかりません',
      body: 'リンクを確認するかホームへ戻ってください。',
      backHome: 'ホームへ戻る',
    },
    quest: {
      backToList: '任務一覧へ戻る',
      backToDetail: '任務詳細へ戻る',
      hidden: '非表示任務',
      favorite: 'お気に入り',
      favorited: 'お気に入り済み',
      readerMode: '読書モード',
      region: '地域',
      chapter: '章',
      recommendLevel: '推奨レベル',
      dialogCount: '会話行数',
      needLevel: '必要レベル',
      cutscenes: 'ムービー',
      cutscenesYes: 'あり',
      cutscenesNo: 'なし',
      preQuests: '前提任務',
      noPreQuests: '前提任務なし',
      nextQuests: '後続任務',
      noNextQuests: '後続任務なし',
      tabs: {
        readable: '読み物',
        dialogs: '会話',
        flow: 'フロー',
        narration: 'ナレーション',
        cutscenes: 'ムービー',
        rewards: '報酬',
        raw: '生 JSON',
      },
      noQuestData: '任務データがありません',
      dialogBrowse: '会話ブラウザ',
      allSpeakers: '全キャラ',
      searchDialogPlaceholder: 'この任務の会話を検索',
      noDialogMatch: '一致する会話がありません',
      unmappedDialogs: '未マッピング会話',
      flowTitle: 'フロー',
      flowHidden: '非表示ステップ',
      flowPublic: '公開ステップ',
      conditionDetails: '条件詳細',
      narrationTitle: 'ナレーション',
      noNarration: 'ナレーションなし',
      cutscenesTitle: 'ムービー',
      noCutscenes: 'ムービーなし',
      rewardsTitle: '報酬',
      noRewards: '報酬なし',
      rawTitle: '生 JSON',
      copyJson: 'JSON をコピー',
      downloadSubtitle: '字幕をダウンロード',
    },
    reader: {
      title: '{{title}} · 読書モード · OnlineQuest',
      fallbackTitle: '任務 {{id}}',
      themeToggle: 'テーマ',
      copyText: 'プレーンテキストをコピー',
      copyMarkdown: 'Markdown をコピー',
      fontSize: '文字サイズ',
      lineHeight: '行間',
      dialogAndNarration: '会話とナレーション',
      stepLabel: 'ステップ {{step}}：サブ任務 {{subQuestId}}',
      noDialogInStep: '（このステップに会話はありません）',
      narrationLabel: '［ナレーション］{{text}}',
      stepHeader: 'サブ任務 {{subQuestId}} · Step {{step}}',
      narrationSection: 'ナレーション',
      speakerFallback: '不明',
      branchLabel: '分岐 {{index}}',
    },
    questJson: {
      speakerUnknown: '不明',
      nodeLabel: 'ノード {{id}}',
      branchLabel: '分岐 {{index}}',
      noDialogInBranch: 'この分岐に表示できる会話はありません。',
      noJoinDetected: '合流点が見つかりません。ここで終了します。',
      noDialogNode: '表示できるノードがありません。',
      noQuestData: '任務データがありません',
      infoTitle: '任務情報',
      infoType: '任務種別',
      infoChapter: '章',
      infoRegion: '地域',
      infoRecommend: '推奨レベル',
      infoNeed: '必要レベル',
      infoMainQuestId: 'メイン任務 ID',
      infoPreMainQuest: '前提メイン任務',
      infoNextMainQuest: '後続メイン任務',
      none: 'なし',
      infoRewards: '報酬',
      noRewards: '報酬なし',
      flowTitle: '任務フロー',
      noFlow: 'フローなし',
      stepTitle: 'ステップ {{order}}：サブ任務 {{subQuestId}}',
      expandConditions: '条件を展開',
      cutsceneTitle: 'ムービー',
      cutsceneFallback: '不明な番号',
      dialogTitle: '会話',
      noDialogStep: '（このステップに会話はありません）',
      startNode: '開始ノード {{id}}',
    },
    questCard: {
      chapterFallback: '未分類の章',
      chapterTitleFallback: '章名未設定',
      recommendLevel: '推奨レベル',
      hidden: '非表示',
      dialogCount: '会話 {{count}}',
      cutsceneHas: 'ムービーあり',
      cutsceneNone: 'ムービーなし',
      favorite: 'お気に入り',
      favorited: 'お気に入り済み',
    },
    chapterCard: {
      uncategorized: '未分類の章',
      unnamed: '章名未設定',
      questCount: '{{count}} 件',
    },
    questSection: {
      questCount: '{{count}} 件',
      collapse: '折りたたむ',
      expand: '展開',
    },
    questCondition: {
      conditionLabel: '条件 {{index}}',
      finishCond: '完了条件',
      triggerCond: 'トリガー条件',
      execActions: '実行アクション',
      guide: 'ガイド情報',
      actionLabel: 'アクション {{index}}',
      otherFields: 'その他のフィールド',
      expandJson: 'JSON を展開',
      expandConditions: '条件を展開',
    },
    common: {
      questFallback: '任務 {{id}}',
      questsCount: '{{count}} 件',
    },
  },
}

type I18nContextValue = {
  lang: LangCode
  locale: string
  t: (key: string, vars?: Record<string, string | number>) => string
  withLang: (path: string) => string
  langLink: (to: string | { pathname?: string; search?: string; hash?: string }) =>
    | string
    | { pathname?: string; search?: string; hash?: string }
  numberFormat: (value: number) => string
  questTypeLabel: (questType: string) => string
  stripLang: (path: string) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

const resolveTranslation = (value: TranslationValue, keyPath: string[]): string | null => {
  if (typeof value === 'string') return value
  const [head, ...rest] = keyPath
  if (!head || typeof value !== 'object') return null
  const next = (value as Record<string, TranslationValue>)[head]
  if (!next) return null
  return resolveTranslation(next, rest)
}

const interpolate = (template: string, vars: Record<string, string | number>) => {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : ''
  )
}

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  const lang = getLangFromPath(location.pathname)
  const config = getLanguageConfig(lang)

  const t = useCallback(
    (key: string, vars: Record<string, string | number> = {}) => {
      const segments = key.split('.')
      const value = resolveTranslation(translations[lang], segments)
      if (!value) {
        return key
      }
      return Object.keys(vars).length > 0 ? interpolate(value, vars) : value
    },
    [lang]
  )

  const withLang = useCallback((path: string) => buildPathWithLang(path, lang), [lang])

  const langLink = useCallback(
    (to: string | { pathname?: string; search?: string; hash?: string }) => {
      if (typeof to === 'string') return withLang(to)
      if (!to.pathname) return to
      return { ...to, pathname: withLang(to.pathname) }
    },
    [withLang]
  )

  const numberFormat = useCallback(
    (value: number) => new Intl.NumberFormat(config.locale).format(value),
    [config.locale]
  )

  const value = useMemo<I18nContextValue>(
    () => ({
      lang,
      locale: config.locale,
      t,
      withLang,
      langLink,
      numberFormat,
      questTypeLabel: (questType: string) => getQuestTypeLabel(lang, questType),
      stripLang: stripLangPrefix,
    }),
    [config.locale, lang, t, withLang, langLink, numberFormat]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => {
  const context = useContext(I18nContext)
  if (!context) {
    return {
      lang: DEFAULT_LANG,
      locale: getLanguageConfig(DEFAULT_LANG).locale,
      t: (key: string) => key,
      withLang: (path: string) => path,
      langLink: (to: string | { pathname?: string; search?: string; hash?: string }) => to,
      numberFormat: (value: number) =>
        new Intl.NumberFormat(getLanguageConfig(DEFAULT_LANG).locale).format(value),
      questTypeLabel: (questType: string) => getQuestTypeLabel(DEFAULT_LANG, questType),
      stripLang: stripLangPrefix,
    }
  }
  return context
}
